import { type QuoteDocument, type QuoteLine, type QuotePhoto } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { type FC, useMemo, useState } from 'react';

import { CustomerProposalExpired } from './CustomerProposalExpired';
import { CustomerProposalReview } from './CustomerProposalReview';
import { CustomerProposalSign } from './CustomerProposalSign';
import { CustomerProposalWorkflow } from './CustomerProposalWorkflow';
import { type EvidencePhoto } from './photoGroups';

export interface CustomerProposalProps {
  customer: {
    firstname?: string;
    lastname?: string;
    businessname?: string;
    email?: string;
    phone?: string;
  };
  preparedBy: {
    name: string;
    title?: string;
    email: string;
    phone: string;
  };
  companyInfo: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  };
  property?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  services: QuoteLine[];
  technicalNotes?: string;
  quotePhotos?: QuotePhoto[];
  evidencePhotos?: EvidencePhoto[];
  proposalValidity: {
    validUntil: Date;
    agreementDuration: Date;
  };
  onComplete: (data: {
    acceptedServices: number[];
    signature: string;
    proposedBy: string;
    acceptedBy: string;
    signatureDate: string;
  }) => void;
  isSubmitting?: boolean;
  quoteDocument?: QuoteDocument;
  discount?: string; // Discount percentage from event
  discountCost?: string; // Flat discount amount from event
  taxRate?: string; // Tax rate percentage from event
}

const CustomerProposal: FC<CustomerProposalProps> = ({
  customer,
  preparedBy,
  companyInfo,
  property,
  services,
  technicalNotes,
  quotePhotos,
  evidencePhotos,
  proposalValidity,
  onComplete,
  quoteDocument,
  isSubmitting = false,
  discount,
  discountCost,
  taxRate,
}) => {
  const [step, setStep] = useState<'review' | 'sign'>('review');
  const [selectedServices, setSelectedServices] = useState<number[]>([]);

  // Parent owns all sign form data - prefill Signed By with customer name
  const defaultSignedBy =
    [customer.firstname, customer.lastname].filter(Boolean).join(' ').trim() ||
    customer.businessname ||
    '';
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptedBy, setAcceptedBy] = useState(defaultSignedBy);
  const [signature, setSignature] = useState('');

  // Check if proposal has expired and hasn't been signed
  const isExpired = useMemo(() => {
    if (!quoteDocument?.quoteExpireDate) {
      return false; // No expiration date set, so not expired
    }

    const expirationDate = new Date(quoteDocument.quoteExpireDate);
    const today = new Date();

    // Reset time to compare dates only
    const expirationDateOnly = new Date(
      expirationDate.getFullYear(),
      expirationDate.getMonth(),
      expirationDate.getDate(),
    );
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Check if today is after the expiration date
    const hasExpired = todayOnly > expirationDateOnly;

    // Check if the proposal has been signed
    const hasBeenSigned =
      quoteDocument.signedDate &&
      quoteDocument.signedDate !== '0001-01-01 00:00:00' &&
      quoteDocument.signedDate !== '';

    // Return true if expired AND not signed
    return hasExpired && !hasBeenSigned;
  }, [quoteDocument]);

  // If expired and not signed, show expired component
  if (isExpired && quoteDocument?.quoteExpireDate) {
    return (
      <CustomerProposalExpired
        logoUrl={companyInfo.logo}
        expirationDate={new Date(quoteDocument.quoteExpireDate)}
      />
    );
  }

  const handleReviewComplete = () => {
    setStep('sign');
  };

  const handleServicesChange = (acceptedServices: number[]) => {
    setSelectedServices(acceptedServices);
  };

  const handleStepChange = (step: string) => {
    if (step === 'review' || step === 'sign') {
      setStep(step);
    }
  };

  const handleSubmit = () => {
    // Validate and scroll to first incomplete field
    if (!acceptTerms) {
      const termsSection = document.getElementById('terms-section');
      termsSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      toast({
        title: 'Terms & Conditions Required',
        description: 'Please accept the terms and conditions to continue.',
        variant: 'destructive',
      });
      return;
    }

    if (!signature) {
      const signatureCanvas = document.getElementById('signature-canvas');
      signatureCanvas?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      toast({
        title: 'Signature Required',
        description: 'Please provide your signature to continue.',
        variant: 'destructive',
      });
      return;
    }

    if (!acceptedBy.trim()) {
      const signedByInput = document.getElementById('signed-by-input');
      signedByInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Delay focus to ensure scroll completes and focus sticks
      setTimeout(() => {
        signedByInput?.focus();
      }, 500);
      toast({
        title: 'Name Required',
        description: 'Please enter your name in the "Signed By" field.',
        variant: 'destructive',
      });
      return;
    }

    // All validation passed, proceed with submission
    onComplete({
      acceptedServices: selectedServices,
      signature,
      proposedBy: preparedBy.name,
      acceptedBy,
      signatureDate: new Date().toISOString().split('T')[0],
    });
  };

  return (
    <CustomerProposalWorkflow
      step={step}
      onStepChange={handleStepChange}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      steps={[
        {
          key: 'review',
          label: 'Review',
          content: (
            <CustomerProposalReview
              customer={customer}
              preparedBy={preparedBy}
              companyInfo={companyInfo}
              property={property}
              services={services}
              technicalNotes={technicalNotes}
              quotePhotos={quotePhotos}
              evidencePhotos={evidencePhotos}
              proposalValidity={proposalValidity}
              onNext={handleReviewComplete}
              onServicesChange={handleServicesChange}
              quoteDocument={quoteDocument}
              initialSelectedServices={selectedServices}
              discount={discount}
              discountCost={discountCost}
              taxRate={taxRate}
            />
          ),
          nextDisabled: selectedServices.length === 0,
        },
        {
          key: 'sign',
          label: 'Sign',
          content: (
            <CustomerProposalSign
              selectedServices={services.filter((s) => selectedServices.includes(s.id))}
              proposalValidity={proposalValidity}
              preparedByName={preparedBy.name}
              acceptTerms={acceptTerms}
              onAcceptTermsChange={setAcceptTerms}
              acceptedBy={acceptedBy}
              onAcceptedByChange={setAcceptedBy}
              signature={signature}
              onSignatureChange={setSignature}
              discount={discount}
              discountCost={discountCost}
              taxRate={taxRate}
            />
          ),
          nextDisabled: false,
        },
      ]}
    />
  );
};

export { getLineDiscount, roundToCents } from './discount';
export {
  groupBySystem,
  groupEvidencePhotosBySystem,
  ADDITIONAL_PHOTOS_LABEL,
  type EvidencePhoto,
  type EvidenceSystemGroup,
  type SystemGroup,
} from './photoGroups';
export { CustomerProposal, CustomerProposalReview, CustomerProposalSign };
export { CustomerProposalComplete } from './CustomerProposalComplete';
export { CustomerProposalExpired } from './CustomerProposalExpired';
export {
  CustomerProposalJobDetails,
  CustomerProposalSinglePageJobDetails,
  CustomerProposalCompleteJobDetails,
  CustomerProposalExpiredJobDetails,
} from './job-details';
